import React from "react";
import { Button, Modal, ModalFooter } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import {
  centralPointSectionTitleKey,
  structureNameLabelKey,
} from "../../../admin/features/structures/schemas/structures.schema";
import {
  GeneralDashboardGeneralData,
  GeneralStructureType,
} from "../../api/generalDashboard/generalDashboard.types";
import { formatGeneralDisplayValue } from "../../utils/formatGeneralDisplayValue";
import {
  GeneralDashboardModalDetailField,
  GeneralDashboardModalSection,
} from "../GeneralDashboardModalFields";

interface GeneralGeneralDataDetailsModalProps {
  open: boolean;
  onClose: () => void;
  structureName: string;
  structureType: GeneralStructureType;
  data: GeneralDashboardGeneralData | null;
}

const orthophotoDisplayName = (
  storagePath: string | null | undefined,
): string | null => {
  if (!storagePath) {
    return null;
  }
  const segments = storagePath.split("/");
  return segments[segments.length - 1] || storagePath;
};

export const GeneralGeneralDataDetailsModal: React.FC<
  GeneralGeneralDataDetailsModalProps
> = ({ open, onClose, structureName, structureType, data }) => {
  const { translate } = useLocalization();
  const fallback = translate("admin.complexes.details.notProvided");

  const formatCoordinateSystem = (value: string | null | undefined) =>
    value
      ? translate(`admin.structures.generalData.coordinateSystem.${value}`)
      : null;

  const formatOperationalStatus = (value: string | null | undefined) =>
    value ? translate(`admin.structures.operationalStatus.${value}`) : null;

  const formatUtmHemisphere = (value: string | null | undefined) =>
    value ? translate(`admin.structures.generalData.utmHemisphere.${value}`) : null;

  const formatDate = (value: string | null | undefined) =>
    formatGeneralDisplayValue(value, true);

  const utmEnabledLabel =
    data?.utmEnabled === true ? "Sim" : data?.utmEnabled === false ? "Não" : fallback;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translate("general.dashboard.generalData.modalTitle")}
      content={
        <div className="general-dashboard-modal-body general-dashboard-modal-body--narrow">
          <GeneralDashboardModalSection
            title={translate("general.dashboard.generalData.title")}
          >
            <div className="general-dashboard-modal-field-grid">
              <GeneralDashboardModalDetailField
                label={translate(structureNameLabelKey(structureType))}
                value={structureName}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.type")}
                value={translate(`admin.structures.structureType.${structureType}`)}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.responsibleCompany")}
                value={data?.responsibleCompany}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.operationalStatus")}
                value={formatOperationalStatus(data?.operationalStatus)}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.operationStartDate")}
                value={formatDate(data?.operationStartDate)}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.operationEndDate")}
                value={formatDate(data?.operationEndDate)}
                fallback={fallback}
              />
            </div>
          </GeneralDashboardModalSection>

          <GeneralDashboardModalSection
            title={translate(centralPointSectionTitleKey(structureType))}
          >
            <div className="general-dashboard-modal-field-grid">
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.centralPointSystem")}
                value={formatCoordinateSystem(data?.centralPointCoordinateSystem)}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.centralPointE")}
                value={data?.centralPointE}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.centralPointN")}
                value={data?.centralPointN}
                fallback={fallback}
              />
            </div>
          </GeneralDashboardModalSection>

          <GeneralDashboardModalSection
            title={translate("admin.structures.generalData.section.pin")}
          >
            <div className="general-dashboard-modal-field-grid">
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.pinSystem")}
                value={formatCoordinateSystem(data?.pinCoordinateSystem)}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.pinE")}
                value={data?.pinE}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.pinN")}
                value={data?.pinN}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.utmEnabled")}
                value={utmEnabledLabel}
                fallback={fallback}
              />
              {data?.utmEnabled ? (
                <>
                  <GeneralDashboardModalDetailField
                    label={translate("admin.structures.generalData.label.utmZone")}
                    value={data.utmZone ?? null}
                    fallback={fallback}
                  />
                  <GeneralDashboardModalDetailField
                    label={translate("admin.structures.generalData.label.utmHemisphere")}
                    value={formatUtmHemisphere(data.utmHemisphere)}
                    fallback={fallback}
                  />
                </>
              ) : null}
              <GeneralDashboardModalDetailField
                label={translate("admin.structures.generalData.label.orthophoto")}
                value={orthophotoDisplayName(data?.orthophotoStoragePath)}
                fallback={fallback}
              />
            </div>
          </GeneralDashboardModalSection>

          <ModalFooter
            footer={
              <Button type="button" variant="primary" onClick={onClose}>
                {translate("admin.complexes.button.close")}
              </Button>
            }
          />
        </div>
      }
    />
  );
};
